import { useRef, useState } from 'react';
import { PageHeader } from '../components/Layout.jsx';
import { Alert, Card, Empty, ErrorState, Input, Loading, Stat } from '../components/ui.jsx';
import { Modal } from '../components/Modal.jsx';
import { IconDownload, IconRefresh, IconTrash } from '../components/Icons.jsx';
import { api, downloadFile } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { useAuth } from '../lib/auth.jsx';
import { useToast } from '../lib/toast.jsx';
import { t } from '../lib/i18n.js';
import { formatDateTime, num } from '../lib/format.js';

const tableLabel = (name) => t.backup.tables[name] || name;

export default function Backup() {
  const { isSuperAdmin } = useAuth();
  const toast = useToast();
  const { data, error, loading, reload } = useApi('/api/backup/summary', null, { enabled: isSuperAdmin });

  const [busy, setBusy] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const fileInput = useRef(null);

  if (!isSuperAdmin) {
    return (
      <>
        <PageHeader title={t.backup.title} back="/settings" />
        <div className="page">
          <Alert tone="warn">{t.backup.adminOnly}</Alert>
        </div>
      </>
    );
  }

  const download = async () => {
    setBusy('download');
    try {
      const { filename } = await downloadFile('/api/backup/export', 'diesel-backup.json');
      toast.success(t.backup.downloaded(filename));
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  const snapshot = async () => {
    setBusy('snapshot');
    try {
      const file = await api.post('/api/backup/snapshot', { note: t.backup.title });
      toast.success(t.backup.snapshotSaved(file.filename));
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  const downloadStored = async (filename) => {
    setBusy(filename);
    try {
      await downloadFile(`/api/backup/files/${encodeURIComponent(filename)}/download`, filename);
      toast.success(t.backup.downloaded(filename));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  const removeStored = async () => {
    setBusy('delete');
    try {
      await api.del(`/api/backup/files/${encodeURIComponent(deleting)}`);
      toast.success(t.backup.snapshotDeleted);
      setDeleting(null);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  const pickFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const doc = JSON.parse(await file.text());
      setRestoring({ source: 'upload', label: file.name, doc });
    } catch {
      toast.error(t.backup.notReadable);
    }
  };

  return (
    <>
      <PageHeader
        title={t.backup.title}
        subtitle={t.backup.subtitle}
        back="/settings"
        actions={
          <button
            type="button"
            className="btn btn-sm btn-ghost no-print"
            onClick={reload}
            aria-label={t.common.refresh}
          >
            <IconRefresh />
          </button>
        }
      />

      <div className="page">
        <ErrorState error={error} onRetry={reload} />
        {loading && !data && <Loading />}

        {data && (
          <>
            <div className="stat-grid">
              <Stat label={t.backup.recordsInDb} value={num(data.total_rows)} />
              <Stat
                label={t.backup.snapshotsOnServer}
                value={num(data.files.length)}
                hint={t.backup.newestKept(num(data.files.length), num(data.keep))}
              />
              <Stat
                label={t.backup.lastBackup}
                value={data.history[0] ? formatDateTime(data.history[0].created_at) : t.common.never}
                hint={data.history[0]?.created_by_name || undefined}
                tone={data.history[0] ? '' : 'danger'}
              />
            </div>

            <Card title={t.backup.takeBackup}>
              <p className="small muted mt-0">{t.backup.contentsNote}</p>
              <Alert tone="warn">{t.backup.passwordWarning}</Alert>
              <div className="btn-row">
                <button type="button" className="btn btn-primary" onClick={download} disabled={busy === 'download'}>
                  {busy === 'download' ? <span className="spinner" /> : <IconDownload />} {t.backup.downloadToDevice}
                </button>
                <button type="button" className="btn" onClick={snapshot} disabled={busy === 'snapshot'}>
                  {busy === 'snapshot' ? <span className="spinner" /> : <IconRefresh />} {t.backup.saveOnServer}
                </button>
              </div>
              <p className="small subtle mt-0">{t.backup.serverNote}</p>
            </Card>

            <Card title={t.backup.whatItContains}>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t.backup.recordType}</th>
                      <th className="num">{t.backup.rows}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tables.map((name) => (
                      <tr key={name}>
                        <td>{tableLabel(name)}</td>
                        <td className="num">{num(data.counts[name] ?? 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>{t.common.total}</td>
                      <td className="num">{num(data.total_rows)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Card>

            <Card title={t.backup.snapshotsOnServer}>
              {data.files.length === 0 ? (
                <Empty title={t.backup.noSnapshots} message={t.backup.noSnapshotsHint} />
              ) : (
                <div className="record-list">
                  {data.files.map((file) => {
                    const isSafetyCopy = file.filename.startsWith('before-restore-');
                    return (
                      <div key={file.filename} className="record">
                        <div className="record-top">
                          <span className="title" dir="ltr">
                            {file.filename}
                          </span>
                          <span className="amount">{formatBytes(file.bytes)}</span>
                        </div>
                        <div className="record-meta">
                          <span>{formatDateTime(file.created_at)}</span>
                          {isSafetyCopy && <span className="badge debt">{t.backup.safetyCopy}</span>}
                        </div>
                        <div className="btn-row no-print" style={{ marginTop: 4 }}>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => downloadStored(file.filename)}
                            disabled={busy === file.filename}
                          >
                            {busy === file.filename ? <span className="spinner" /> : <IconDownload />}{' '}
                            {t.backup.download}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() =>
                              setRestoring({ source: 'server', label: file.filename, filename: file.filename })
                            }
                          >
                            {t.backup.restoreThis}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => setDeleting(file.filename)}
                          >
                            <IconTrash /> {t.common.delete}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            <Card title={t.backup.restoreFromFile}>
              <p className="small muted mt-0">{t.backup.restoreFromFileHint}</p>
              <input
                ref={fileInput}
                type="file"
                accept="application/json,.json"
                onChange={pickFile}
                className="sr-only"
              />
              <button type="button" className="btn" onClick={() => fileInput.current?.click()}>
                {t.backup.chooseFile}
              </button>
            </Card>

            <Card title={t.backup.history}>
              {data.history.length === 0 ? (
                <Empty title={t.backup.nothingRecorded} />
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{t.backup.when}</th>
                        <th>{t.backup.action}</th>
                        <th>{t.backup.by}</th>
                        <th>{t.backup.file}</th>
                        <th>{t.backup.note}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.history.map((row) => (
                        <tr key={row.id}>
                          <td>{formatDateTime(row.created_at)}</td>
                          <td>
                            <span className={`badge ${row.action === 'restore' ? 'danger' : 'buy'}`}>
                              {t.backup.actions[row.action] || row.action}
                            </span>
                          </td>
                          <td>{row.created_by_name || t.common.system}</td>
                          <td className="wrap" dir="ltr">
                            {row.filename || '-'}
                          </td>
                          <td className="wrap">{row.note || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}
      </div>

      {restoring && (
        <RestoreDialog
          request={restoring}
          onClose={() => setRestoring(null)}
          onDone={(result) => {
            setRestoring(null);
            toast.success(t.backup.restored(num(Object.values(result.restored).reduce((a, b) => a + b, 0))));
            reload();
          }}
        />
      )}

      {deleting && (
        <Modal
          title={t.backup.deleteSnapshot}
          onClose={() => setDeleting(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setDeleting(null)} disabled={busy === 'delete'}>
                {t.common.cancel}
              </button>
              <button type="button" className="btn btn-danger" onClick={removeStored} disabled={busy === 'delete'}>
                {busy === 'delete' ? <span className="spinner" /> : t.common.delete}
              </button>
            </>
          }
        >
          <p>{t.backup.deleteMessage(deleting)}</p>
        </Modal>
      )}
    </>
  );
}

/**
 * The confirmation step. A restore cannot be undone from inside the app, so it
 * shows exactly what is about to replace what and makes the operator type the
 * word before the button does anything.
 */
function RestoreDialog({ request, onClose, onDone }) {
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const inspect = useApi(
    request.source === 'server' ? `/api/backup/files/${encodeURIComponent(request.filename)}/inspect` : null,
    null,
    { enabled: request.source === 'server' },
  );

  const header =
    request.source === 'server'
      ? inspect.data
      : {
          generated_at: request.doc?.generated_at,
          generated_by: request.doc?.generated_by,
          facility_name: request.doc?.facility_name,
          counts: request.doc?.counts,
          total_rows: Object.values(request.doc?.counts || {}).reduce((a, b) => a + b, 0),
        };

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { confirm: 'RESTORE' };
      if (request.source === 'server') body.filename = request.filename;
      else body.backup = request.doc;
      onDone(await api.post('/api/backup/restore', body));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t.backup.restoreTitle}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t.common.cancel}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={run}
            disabled={busy || confirm.trim().toUpperCase() !== 'RESTORE' || (request.source === 'server' && !inspect.data)}
          >
            {busy ? <span className="spinner" /> : t.backup.replaceAll}
          </button>
        </>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}

      <Alert tone="error">{t.backup.restoreWarning}</Alert>
      <p className="small muted mt-0">{t.backup.restoreSafety}</p>

      {inspect.loading && !inspect.data && <Loading label={t.backup.reading} />}
      <ErrorState error={inspect.error} />

      {header && (
        <div className="table-wrap">
          <table>
            <tbody>
              <tr>
                <td>{t.backup.file}</td>
                <td className="wrap strong" dir="ltr">
                  {request.label}
                </td>
              </tr>
              <tr>
                <td>{t.backup.takenAt}</td>
                <td className="strong">{formatDateTime(header.generated_at)}</td>
              </tr>
              <tr>
                <td>{t.backup.takenBy}</td>
                <td>{header.generated_by || t.common.unknown}</td>
              </tr>
              {header.facility_name && (
                <tr>
                  <td>{t.backup.facility}</td>
                  <td>{header.facility_name}</td>
                </tr>
              )}
              <tr>
                <td>{t.backup.records}</td>
                <td className="strong">{num(header.total_rows || 0)}</td>
              </tr>
              {header.counts && (
                <tr>
                  <td>{t.backup.contents}</td>
                  <td className="wrap">
                    {Object.entries(header.counts)
                      .filter(([, n]) => n > 0)
                      .map(([name, n]) => `${tableLabel(name)}: ${num(n)}`)
                      .join('، ') || t.backup.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Input
        label={t.backup.typeToConfirm}
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        autoComplete="off"
        autoCapitalize="characters"
        placeholder="RESTORE"
        dir="ltr"
      />
    </Modal>
  );
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return '-';
  if (bytes < 1024) return `${num(bytes)} B`;
  if (bytes < 1024 * 1024) return `${num(bytes / 1024, 1)} kB`;
  return `${num(bytes / 1024 / 1024, 1)} MB`;
}
